// @ai-generated 2026-08-10T06:52:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
import { type ApiOrderSnapshot, EnumStagingStatus } from '@Model';

/**
 * What the linked order snapshots tell us about a requisition's progress. The snapshots are not a
 * status history, so only these few facts can be read off them without guessing.
 */
export interface PortalRequisitionOrderFacts {
    /** When the order reached the operator, i.e. when the requisition entered review. */
    reviewedAt?: string;

    /**
     * When the last of the orders finished execution — the requisition only counts as executed once
     * every order is done. The staging gate leaves no mark of its own, so this is the only date the
     * execution stage can be given once it lies behind us. It is set as soon as execution finishes,
     * which is ahead of the customer's "Abgeschlossen" — only a reached stage may show it.
     */
    executionFinishedAt?: string;

    /**
     * Why the operator rejected the order, as they wrote it in the staging gate. The requisition keeps
     * no reason of its own, so this is the only place the customer's view can read it from.
     */
    rejectionReason?: string;
}

function latest(dates: (string | undefined)[]): string | undefined {
    const known = dates.filter((date): date is string => Boolean(date));
    return known.length === 0 ? undefined : known.reduce((a, b) => (a > b ? a : b));
}

function earliest(dates: (string | undefined)[]): string | undefined {
    const known = dates.filter((date): date is string => Boolean(date));
    return known.length === 0 ? undefined : known.reduce((a, b) => (a < b ? a : b));
}

/**
 * The reasons the operator gave for rejecting this requisition's orders. The timeline shows a single
 * rejection step, so several differing reasons are listed side by side rather than one of them chosen.
 * @ai-generated 2026-08-14T00:00:00Z by matthias.gessinger@synqony.com using Claude Opus 5
 */
function collectRejectionReason(snapshots: readonly ApiOrderSnapshot[]): string | undefined {
    const reasons = snapshots
        .filter((snapshot) => snapshot.staging_status === EnumStagingStatus.Rejected)
        .map((snapshot) => snapshot.staging_remarks.trim())
        .filter(Boolean);

    const distinct = [...new Set(reasons)];
    return distinct.length === 0 ? undefined : distinct.join(' · ');
}

/** Reads the datable facts off a requisition's order snapshots.
 * @ai-generated 2026-08-27T11:14:57+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
export function derivePortalRequisitionOrderFacts(
    snapshots: readonly ApiOrderSnapshot[] | undefined,
): PortalRequisitionOrderFacts {
    if (!snapshots || snapshots.length === 0) {
        return {};
    }

    return {
        reviewedAt: earliest(snapshots.map((snapshot) => snapshot.created_at)),
        executionFinishedAt: latest(snapshots.map((snapshot) => snapshot.completed_at)),
        rejectionReason: collectRejectionReason(snapshots),
    };
}
